import { inject, Injectable } from "@angular/core";
import { CORE_API_URL } from "./injection-tokens";
import { ApiService } from "./api.service";
import { catchError, lastValueFrom, map, of } from "rxjs";
import { Practice } from "../models/practice";
import { Professional } from "../models/professional";
import { ProviderLocation, OrganizationProvider, ProfessionalProvider } from "../models/provider";

export class PracticeFilter {
    name?: string;
    ein?: string;
    location?: string;
}

export class PracticeUpdate {
    name?: string;
}

export class OrganizationProviderInsert {
    npi: string = '';
}

export class OrganizationProviderUpdate {
    organizationName?: string;
}

export class ProfessionalProviderUpdate {
    firstName?: string;
    lastName?: string;
}

export abstract class ProviderServiceBase {
    abstract searchPractices(filter: PracticeFilter): Promise<Practice[]>;
    abstract updatePractice(ein: string, update: PracticeUpdate): Promise<Practice>;
    abstract getOrganzationalProviderLocations(ein: string, npi: string): Promise<ProviderLocation[]>;
    abstract addOrganizationProviderToPractice(ein: string, insert: OrganizationProviderInsert): Promise<OrganizationProvider>;
    abstract removeOrganizationProviderFromPractice(ein: string, npi: string): Promise<void>;
    abstract updateOrganizationProvider(npi: string, update: OrganizationProviderUpdate): Promise<OrganizationProvider>;
    abstract addProfessionalProvider(ein: string, npi: string, locationId: number, professionalNpi: string): Promise<ProfessionalProvider>;
    abstract removeProfessionalProvider(ein: string, npi: string, locationId: number, professionalNpi: string): Promise<ProfessionalProvider>;
    abstract updateProfessionalProvider(npi: string, update: ProfessionalProviderUpdate): Promise<Professional>;
    abstract addProviderLocation(ein: string, npi: string, location: ProviderLocation): Promise<ProviderLocation>;
    abstract linkProviderLocation(ein: string, npi: string, locationId: number): Promise<ProviderLocation>;
    abstract updateProviderLocation(ein: string, npi: string, locationId: number, location: ProviderLocation): Promise<ProviderLocation>;
    abstract removeProviderLocation(ein: string, npi: string, locationId: number): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class ProviderService extends ProviderServiceBase {
    protected _api: ApiService = inject(ApiService);
    protected _baseUrl: string = inject(CORE_API_URL);

    override searchPractices(filter: PracticeFilter): Promise<Practice[]> {
        return lastValueFrom(
            this._api.makeAPIGetCall<any[]>(this._baseUrl, "/providers/practices",
            {
                ...filter.name     && { name:     filter.name },
                ...filter.ein      && { ein:      filter.ein },
                ...filter.location && { location: filter.location }
            })
            .pipe(map(results => results.map(r => new Practice(
                r.name,
                r.ein,
                (r.facilities ?? []).map((f: any) => new OrganizationProvider(f.npi, f.organizationName)),
                r.aliases ?? []
            ))))
        );
    }

    override updatePractice(ein: string, update: PracticeUpdate): Promise<Practice> {
        return lastValueFrom(
            this._api.makeAPIPatchCall<Practice>(this._baseUrl, `/providers/practices/${ein}`, update)
                .pipe(map(r => new Practice(r.name, r.ein, r.facilities, r.aliases ?? [])))
        );
    }

    override getOrganzationalProviderLocations(ein: string, npi: string): Promise<ProviderLocation[]> {
        return lastValueFrom(
            this._api.makeAPIGetCall<any[]>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations`, {})
            .pipe(
                map(results => results.map((l: any) => new ProviderLocation(
                    l.locationId, l.street1, l.city, l.state, l.zipCode,
                    (l.professionals ?? []).map((p: any) => new Professional(p.firstName, p.lastName, p.nPI ?? p.npi)),
                    l.street2
                ))),
                catchError(() => of([]))
            )
        );
    }

    override addOrganizationProviderToPractice(ein: string, insert: OrganizationProviderInsert): Promise<OrganizationProvider> {
        return lastValueFrom(
            this._api.makeAPIPostCall<any>(this._baseUrl, `/providers/practices/${ein}/facilities`, insert)
                .pipe(map(r => new OrganizationProvider(r.npi, r.organizationName)))
        );
    }

    override removeOrganizationProviderFromPractice(ein: string, npi: string): Promise<void> {
        return lastValueFrom(
            this._api.makeAPIDeleteCall<void>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}`)
                .pipe(map(() => void 0))
        );
    }

    override updateOrganizationProvider(npi: string, update: OrganizationProviderUpdate): Promise<OrganizationProvider> {
        return lastValueFrom(
            this._api.makeAPIPatchCall<any>(this._baseUrl, `/providers/organizations/${npi}`, update)
                .pipe(map(r => new OrganizationProvider(r.npi, r.organizationName)))
        );
    }

    override addProfessionalProvider(ein: string, npi: string, locationId: number, professionalNpi: string): Promise<ProfessionalProvider> {
        return lastValueFrom(
            this._api.makeAPIPostCall<any>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations/${locationId}/professionals/${professionalNpi}`, { npi: professionalNpi })
                .pipe(map(r => new ProfessionalProvider(r.npi ?? r.nPI, r.firstName, r.lastName)))
        );
    }

    override removeProfessionalProvider(ein: string, npi: string, locationId: number, professionalNpi: string): Promise<ProfessionalProvider> {
        return lastValueFrom(
            this._api.makeAPIDeleteCall<any>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations/${locationId}/professionals/${professionalNpi}`)
                .pipe(map(r => new ProfessionalProvider(r.npi ?? r.nPI, r.firstName, r.lastName)))
        );
    }

    override updateProfessionalProvider(npi: string, update: ProfessionalProviderUpdate): Promise<Professional> {
        return lastValueFrom(
            this._api.makeAPIPatchCall<any>(this._baseUrl, `/providers/professionals/${npi}`, update)
                .pipe(map(r => new Professional(r.firstName, r.lastName, r.npi ?? r.nPI)))
        );
    }

    override addProviderLocation(ein: string, npi: string, location: ProviderLocation): Promise<ProviderLocation> {
        const insert = {
            address1: location.street1,
            address2: location.street2,
            city: location.city,
            state: location.state,
            zipCode: location.zipCode,
        };
        return lastValueFrom(
            this._api.makeAPIPostCall<any>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations`, insert)
                .pipe(map(l => new ProviderLocation(l.locationId, l.street1, l.city, l.state, l.zipCode,
                    (l.professionals ?? []).map((p: any) => new Professional(p.firstName, p.lastName, p.nPI ?? p.npi)),
                    l.street2)))
        );
    }

    override linkProviderLocation(ein: string, npi: string, locationId: number): Promise<ProviderLocation> {
        return lastValueFrom(
            this._api.makeAPIPostCall<any>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations/${locationId}/link`, {})
                .pipe(map(l => new ProviderLocation(l.locationId, l.street1, l.city, l.state, l.zipCode,
                    (l.professionals ?? []).map((p: any) => new Professional(p.firstName, p.lastName, p.nPI ?? p.npi)),
                    l.street2)))
        );
    }

    override updateProviderLocation(ein: string, npi: string, locationId: number, location: ProviderLocation): Promise<ProviderLocation> {
        const update = {
            address1: location.street1,
            address2: location.street2,
            city: location.city,
            state: location.state,
            zipCode: location.zipCode,
        };
        return lastValueFrom(
            this._api.makeAPIPatchCall<any>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations/${locationId}`, update)
                .pipe(map(l => new ProviderLocation(l.locationId, l.street1, l.city, l.state, l.zipCode,
                    (l.professionals ?? []).map((p: any) => new Professional(p.firstName, p.lastName, p.nPI ?? p.npi)),
                    l.street2)))
        );
    }

    override removeProviderLocation(ein: string, npi: string, locationId: number): Promise<void> {
        return lastValueFrom(
            this._api.makeAPIDeleteCall<void>(this._baseUrl, `/providers/practices/${ein}/facilities/${npi}/locations/${locationId}`)
                .pipe(map(() => void 0))
        );
    }

}
